import { TableScroll } from '@web/components/table-scroll.tsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { singleJobApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { getDateLocale } from '../../i18n/locale.ts';
import { Alert, EmptyState, LoadingBlock, Pagination } from '../ui.tsx';
import { StarterNudge } from '../starter-nudge.tsx';
import { QuotePaymentBadge } from '../payments/quote-payments.tsx';
import { formatJobStart } from './single-job-card.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import type { BookedJob } from '@shared/types/index.ts';

/** The pseudo-status the quotes list filters on for this view (and `?filter=booked`). */
export const BOOKED_FILTER = 'booked-jobs';

/**
 * Every job with a visit still to happen, by date: the Booked filter on the
 * quotes list (`single_job`, every plan). A plain list, not a calendar — the
 * calendar is the paid plans' — with the day and time, the customer, the
 * amount and whether it is paid. A Book again copy is its own row, with a
 * Repeat tag that says which job it came from.
 */
export function BookedJobsList() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const booked = useQuery({
    queryKey: ['booked-jobs', { page }],
    queryFn: () => singleJobApi.booked({ page, pageSize: 25 }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <StarterNudge className="mb-4" id="booked-calendar" message={t('bookedJobs.starter')} />
      {booked.isLoading ? <LoadingBlock label={t('bookedJobs.loading')} /> : null}
      {booked.isError ? <Alert tone="error">{(booked.error as Error).message}</Alert> : null}
      {booked.data && booked.data.items.length === 0 ? (
        <EmptyState title={t('bookedJobs.emptyTitle')}>
          <p>{t('bookedJobs.empty')}</p>
        </EmptyState>
      ) : null}
      {booked.data && booked.data.items.length > 0 ? (
        <>
          <TableScroll
            aria-label={t('bookedJobs.title')}
            className="overflow-x-auto rounded-lg border border-[color:var(--color-line)] bg-white"
          >
            <table className="data-table w-full min-w-max text-left text-sm">
              <thead className="bg-[color:var(--color-surface)] text-[color:var(--color-muted)]">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t('bookedJobs.when')}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t('bookedJobs.customer')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    {t('bookedJobs.amount')}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t('bookedJobs.paid')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {booked.data.items.map((row) => (
                  <BookedRow key={row.quoteId} row={row} />
                ))}
              </tbody>
            </table>
          </TableScroll>
          <div className="mt-4">
            <Pagination page={page} totalPages={booked.data?.totalPages} onPageChange={setPage} />
          </div>
        </>
      ) : null}
    </>
  );
}

function BookedRow({ row }: { row: BookedJob }) {
  const { t } = useTranslation();
  return (
    <tr className="border-t border-[color:var(--color-line)]">
      <td className="px-3 py-2 whitespace-nowrap">
        <Link to={`/quotes/${row.quoteId}`} className="font-semibold text-[color:var(--color-brand)] hover:underline">
          {formatJobStart(row.startsAt, row.timezone)}
        </Link>
      </td>
      <td className="px-3 py-2">
        <span>{row.customerName}</span>
        {row.sourceQuote ? <RepeatTag from={row.sourceQuote.quoteNumber} /> : null}
        <span className="block text-xs text-[color:var(--color-muted)]">{row.quoteNumber}</span>
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {row.amountCents === null ? '' : formatCents(row.amountCents, row.currencyCode, getDateLocale())}
      </td>
      <td className="px-3 py-2">
        <QuotePaymentBadge status={row.paymentStatus} />
      </td>
    </tr>
  );
}

/** "Repeat", which says "From Q-1042" when hovered or tapped. */
function RepeatTag({ from }: { from: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = t('bookedJobs.repeatFrom', { number: from });
  return (
    <button
      type="button"
      title={label}
      aria-label={`${t('bookedJobs.repeat')}: ${label}`}
      aria-expanded={open}
      onClick={() => setOpen((current) => !current)}
      className="badge ml-2 border-[color:var(--color-line)] text-[color:var(--color-muted)]"
    >
      {open ? label : t('bookedJobs.repeat')}
    </button>
  );
}

/**
 * The Free plan's dashboard view of its diary: the next ten booked jobs, and
 * the way to all of them.
 */
export function BookedJobsCard() {
  const { t } = useTranslation();
  const { can } = useSession();
  const booked = useQuery({
    queryKey: ['booked-jobs', { page: 1, pageSize: 10 }],
    queryFn: () => singleJobApi.booked({ page: 1, pageSize: 10 }),
    enabled: can('quotes:read'),
  });
  if (!can('quotes:read')) return null;
  return (
    <section className="card" aria-labelledby="booked-jobs-heading">
      <h2 id="booked-jobs-heading" className="mb-3 text-lg font-semibold">
        {t('bookedJobs.title')}
      </h2>
      {booked.isLoading ? <p className="text-sm text-[color:var(--color-muted)]">{t('bookedJobs.loading')}</p> : null}
      {booked.isError ? <Alert tone="error">{(booked.error as Error).message}</Alert> : null}
      {booked.data && booked.data.items.length === 0 ? (
        <p className="text-sm text-[color:var(--color-muted)]">{t('bookedJobs.dashboardEmpty')}</p>
      ) : null}
      {booked.data && booked.data.items.length > 0 ? (
        <ul className="grid gap-2">
          {booked.data.items.map((row) => (
            <li key={row.quoteId} className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <Link to={`/quotes/${row.quoteId}`} className="text-[color:var(--color-brand)] hover:underline">
                {formatJobStart(row.startsAt, row.timezone)} · {row.customerName}
              </Link>
              <span className="flex items-center gap-2">
                {row.amountCents === null ? null : (
                  <span>{formatCents(row.amountCents, row.currencyCode, getDateLocale())}</span>
                )}
                <QuotePaymentBadge status={row.paymentStatus} />
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      <Link to={`/quotes?filter=booked`} className="mt-3 inline-block text-sm font-semibold underline">
        {t('bookedJobs.seeAll')}
      </Link>
    </section>
  );
}
